import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import type { InboxViewTimeRange } from "@/types/inboxView/inboxView";

interface Props {
  value: InboxViewTimeRange;
  onChange: (next: InboxViewTimeRange) => void;
  disabled?: boolean;
}

const OPTIONS: Array<{ value: "last_7_days" | "last_14_days" | "last_30_days" | "last_90_days"; label: string }> = [
  { value: "last_7_days",  label: "Last 7 days" },
  { value: "last_14_days", label: "Last 14 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
];

export const TimeRangePicker = ({ value, onChange, disabled }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = value.kind === "rolling"
    ? OPTIONS.find((o) => o.value === value.value)?.label ?? "Last 30 days"
    : "Custom range";

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border bg-white border-[#E5EAF0] text-[#222B45] hover:border-genie-primary/40 hover:text-genie-primary transition-colors disabled:opacity-60"
      >
        <span className="text-[#64748B]">When:</span>
        <span className="font-semibold">{current}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute z-30 mt-2 w-56 bg-white border border-[#E5EAF0] rounded-xl shadow-xl overflow-hidden"
          style={{ boxShadow: "0 12px 32px -12px rgba(15, 23, 42, .25)" }}
        >
          <div className="px-3 py-2 border-b border-[#E5EAF0]">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#64748B]">Time range</span>
          </div>
          <div className="py-1">
            {OPTIONS.map((opt) => {
              const isOn = value.kind === "rolling" && value.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange({ kind: "rolling", value: opt.value });
                    setOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#FAFCFF] transition-colors"
                >
                  <span className={`h-4 w-4 rounded-md border flex items-center justify-center ${isOn ? "bg-genie-primary border-genie-primary" : "border-[#E5EAF0]"}`}>
                    {isOn ? <Check className="h-3 w-3 text-white" strokeWidth={3} /> : null}
                  </span>
                  <span className={isOn ? "font-semibold text-[#222B45]" : "text-[#222B45]"}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
